import { useForm } from '@tanstack/react-form';
import { log } from 'evlog/client';
import { Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { GetSecondOpinionById } from '@virtual-care/client';
import { AttachFilesField, uploadFile } from '@virtual-care/media-web';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Textarea,
} from '@virtual-care/shadcn';
import {
  FieldMessage,
  FormMessage,
  errorToFormIssue,
  useAction,
} from '@virtual-care/ui';

import * as inputs from '../../../components/inputs.ts';

const schema = z
  .object({
    details: z.string().optional().default(''),
    files: inputs.files.optional().default([]),
  })
  .refine(
    (data) =>
      (data.details && data.details.length > 0) ||
      (data.files && data.files.length > 0),
    {
      message: 'secondOpinion.submitInfo.validation',
    },
  );

interface SubmitInfoButtonProps {
  secondOpinion: GetSecondOpinionById;
}

export function SubmitInfoButton({ secondOpinion }: SubmitInfoButtonProps) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const submitInfo = useAction('POST /second-opinions/{id}/submit-info', {
    invalidate: [
      'GET /patients/me/second-opinions',
      'GET /second-opinions/{id}',
    ],
  });

  const moreInfoNeeded = secondOpinion.timeline.find(
    (event) => event.type === 'MORE_INFO_NEEDED',
  );
  const requestedInfo =
    moreInfoNeeded && moreInfoNeeded.context
      ? JSON.parse(moreInfoNeeded.context).requestedInfo
      : '';

  const form = useForm({
    defaultValues: {
      details: '',
      files: [] as File[],
    } as z.input<typeof schema>,
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          log.info({
            action: 'submit_info',
            secondOpinionId: secondOpinion.id,
            fileCount: value.files?.length ?? 0,
          });
          const fileIds = await Promise.all(
            (value.files ?? []).map((file) => uploadFile(file)),
          );

          await submitInfo.mutateAsync({
            id: secondOpinion.id,
            details: value.details || undefined,
            files: fileIds.length > 0 ? fileIds : undefined,
          });
          setOpen(false);
          form.reset();
          return void 0;
        } catch (error) {
          log.error({
            action: 'submit_info',
            secondOpinionId: secondOpinion.id,
            error,
          });
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-secondary-foreground text-secondary-foreground cursor-pointer gap-2 text-sm font-bold"
        >
          {t('secondOpinion.submitInfo.trigger')} <Send className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>{t('secondOpinion.submitInfo.title')}</DialogTitle>
          {requestedInfo && (
            <DialogDescription className="text-start">
              <strong>
                {t('secondOpinion.submitInfo.doctorRequestedPrefix')}
              </strong>
              <br />
              {requestedInfo}
            </DialogDescription>
          )}
        </DialogHeader>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <div className="space-y-4 py-4">
            <form.Field
              name="details"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label htmlFor="details" className="text-sm">
                    {t('secondOpinion.submitInfo.details')}
                  </Label>
                  <Textarea
                    id="details"
                    placeholder={t(
                      'secondOpinion.submitInfo.detailsPlaceholder',
                    )}
                    rows={6}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={[field.state.meta.errors[0]]} />
                </div>
              )}
            />

            <form.Field
              name="files"
              children={(field) => <AttachFilesField field={field} />}
            />
          </div>

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />

          <DialogFooter>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <Button type="submit" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting
                    ? t('secondOpinion.submitInfo.submitting')
                    : t('secondOpinion.submitInfo.submit')}
                </Button>
              )}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
